import { observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import type { AlertErrorHandlerFactory } from '@/features/shell'
import { type JobNoteStore } from '@/features/jobs/details/notes/JobNoteStore'
import { AsyncPrompt } from '@/infra/AsyncPrompt'
import { Id } from '@/infra/id'
import { ModalViewModel } from '@/infra/ModalViewModel'

/**
 * Confirms and deletes a job note.
 */
export class DeleteJobNoteDialogViewModel {
  private readonly _prompt

  @observable private accessor _noteId: Id = Id.none

  readonly modal = new ModalViewModel()

  constructor(
    private readonly _store: JobNoteStore,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {
    this._prompt = new AsyncPrompt<boolean>({
      abort$: this.modal.close$,
    })
  }

  /**
   * Asks the user to confirm, and deletes the note once they do.
   *
   * @returns Whether the note was deleted.
   */
  async prompt(noteId: Id, signal?: AbortSignal) {
    this.modal.close()

    const responsePromise = this._prompt.prompt(signal)
    runInAction(() => {
      this._noteId = noteId
      this.modal.show()
    })

    const response = await responsePromise
    this.modal.close()
    return response ?? false
  }

  /**
   * Deletes the note and resolves the prompt.
   */
  delete = task.resolved(async () => {
    await this._store
      .archive(this._noteId)
      .catch(this._createAlertErrorHandler('Deleting the note failed'))

    this._prompt.respond(true)
    this.modal.close()
  })
}
